import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * ThemeContext - persist user preference in localStorage.
 * Default: system preference (prefers-color-scheme), falling back to "dark".
 * The theme value is applied to the <html> element via `data-theme=`.
 * CSS variables defined in index.css automatically flip when the attribute changes.
 */

const STORAGE_KEY = "pr_theme_v1";

const ThemeCtx = createContext(null);

function readInitial() {
    if (typeof window === "undefined") return "dark";
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === "light" || stored === "dark") return stored;
    } catch {
        /* ignore */
    }
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches) {
        return "light";
    }
    return "dark";
}

export function ThemeProvider({ children }) {
    const [theme, setTheme] = useState(readInitial);

    // Apply to <html>
    useEffect(() => {
        if (typeof document !== "undefined") {
            document.documentElement.setAttribute("data-theme", theme);
            // Also set color-scheme so form controls / scrollbars follow theme
            document.documentElement.style.colorScheme = theme;
            // Keep the browser chrome / status-bar tint in sync with the app
            // theme. Without this the phone's status bar area stays dark
            // (#040914) even in light mode, showing up as a visible "black
            // margin" at the top of the viewport on Android Chrome & installed
            // PWAs. We rewrite BOTH the theme-color meta (browser chrome) and
            // the msapplication-TileColor (Windows tile) if present, and add
            // one when missing.
            try {
                const chromeColor = theme === "light" ? "#ece9e0" : "#040914";
                let meta = document.querySelector('meta[name="theme-color"]');
                if (!meta) {
                    meta = document.createElement("meta");
                    meta.setAttribute("name", "theme-color");
                    document.head.appendChild(meta);
                }
                meta.setAttribute("content", chromeColor);
                const tile = document.querySelector('meta[name="msapplication-TileColor"]');
                if (tile) tile.setAttribute("content", chromeColor);
            } catch {
                /* ignore */
            }
        }
        try {
            localStorage.setItem(STORAGE_KEY, theme);
        } catch {
            /* ignore */
        }
    }, [theme]);

    const toggle = useCallback(() => {
        setTheme((t) => (t === "dark" ? "light" : "dark"));
    }, []);

    const value = useMemo(() => ({ theme, setTheme, toggle }), [theme, toggle]);
    return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
    const ctx = useContext(ThemeCtx);
    if (!ctx) {
        // Fallback for components used outside the provider (e.g. tests).
        return { theme: "dark", setTheme: () => {}, toggle: () => {} };
    }
    return ctx;
}
